import * as M from "reports/measures/Measure";
import * as MD from "reports/measures/MeasureDef";
import * as RD from "reports/ReportDef";
import * as SR from "reports/measures/SeverityResolver";

/**
 * Calculates return to work status from work measures.
 *
 * @param workMeasures - Array of work measures
 * @returns Return to work status or null if no measures
 */
export function calculateReturnToWorkStatus(
    workMeasures: MD.Measures
): RD.ReturnToWorkStatus | null {
    if (workMeasures.length === 0) {
        return null;
    }

    const mostRecentMeasure: MD.Measure = workMeasures[workMeasures.length - 1];
    const resolver: SR.SeverityDetails = M.severityResolver(mostRecentMeasure);

    return {
        week: mostRecentMeasure.week,
        value: mostRecentMeasure.value,
        label: resolver.label
    };
}

/**
 * Calculates medication reduction status from medication measures.
 *
 * @param medicationMeasures - Array of medication measures
 * @returns Medication reduction status or null if no measures
 */
export function calculateMedicationReductionStatus(
    medicationMeasures: MD.Measures
): RD.MedicationReductionStatus | null {
    if (medicationMeasures.length === 0) {
        return null;
    }

    const currentWeekValue = medicationMeasures[medicationMeasures.length - 1].value;

    const wasMedicationReduced = (measures: MD.Measures, currentWeekValue: number): number => {
        const areAllValuesZero = (measuresArray: MD.Measures): boolean => {
            if (measuresArray.length === 0) {
                return true;
            }
            return measuresArray.every(measure => measure.value === 0);
        };

        // --- Conditions for "No" ---
        // 1. Answer value for "Medication" in the report week's Survey is 2 or higher, OR
        // 2. All answer values for "Medication" in the current and previous weeks are 0 (indicating patient never took medication).
        const isCurrentWeekValueTwoOrHigher = currentWeekValue >= 2;
        const areAllMeasuresZeroIncludingCurrent = areAllValuesZero(measures);
        if (isCurrentWeekValueTwoOrHigher || areAllMeasuresZeroIncludingCurrent) {
            return 0;
        }

        // --- Conditions for "Yes" ---
        // 1. Answer value for "Medication" in the report week's Survey was 1 or lower, AND
        // 2. "Medication" values in previous weeks are not all 0
        const previousWeeksMeasures = measures.slice(0, measures.length - 1);
        const arePreviousWeeksNotAllZero = !areAllValuesZero(previousWeeksMeasures);

        if (arePreviousWeeksNotAllZero) {
            return 1;
        }

        // If none of the "No" conditions were met, and the "Yes" conditions were not fully met,
        // it implies `currentWeekValue` is 0 or 1, but there was no medication taken in previous weeks.
        // In this specific scenario, it should report "No" (as per the "never took medication" clause).
        return 0;
    };

    let mostRecentMeasure: MD.Measure = medicationMeasures[medicationMeasures.length - 1];
    mostRecentMeasure.value = wasMedicationReduced(medicationMeasures, currentWeekValue);
    const resolver: SR.SeverityDetails = M.severityResolver(mostRecentMeasure);

    return {
        week: mostRecentMeasure.week,
        value: mostRecentMeasure.value,
        label: resolver.label
    };
}
